import React, { useState, useEffect } from "react";
import axiosInstance from '../../../../../api';

const VehicleDailychecklistModal = ({
  vehicleId,
  itemId,
  initialData = {},
  onClose,
  refreshData,
  isSubitem = false, // New prop to detect subitem editing
}) => {
  const [formData, setFormData] = useState({
    name: "",
    criteria: "",
    vehicle_id: vehicleId,
    child: [],
  });
  const [newSubItem, setNewSubItem] = useState({ name: "", criteria: "" });
  const [showSubItemForm, setShowSubItemForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  // Fix: Correct session key name
  const checklistId = sessionStorage.getItem('vehicle_checklist_id');

  // Load initial data
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData({
        name: initialData.name || "",
        criteria: initialData.criteria || "",
        vehicle_id: vehicleId,
        child: initialData.child || initialData.ems_child_vehicle_checklist || [],
      });

      // If already has subitems → show them, but DO NOT show "Add New Subitem" form
      if (
        initialData.child?.length > 0 ||
        initialData.ems_child_vehicle_checklist?.length > 0
      ) {
        setShowSubItemForm(true); // Show existing subitems
      }
    }
  }, [initialData, vehicleId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubItemChange = (e) => {
    const { name, value } = e.target;
    setNewSubItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleExistingSubItemChange = (index, e) => {
    const { name, value } = e.target;
    const newChild = [...formData.child];
    newChild[index] = { ...newChild[index], [name]: value };
    setFormData((prev) => ({ ...prev, child: newChild }));
  };

  const handleAddSubItem = () => {
    if (!newSubItem.name.trim() || !newSubItem.criteria.toString().trim()) {
      setValidated(true);
      return;
    }
    const subItemToAdd = {
      name: newSubItem.name.trim(),
      criteria: Number(newSubItem.criteria),
    };
    setFormData((prev) => ({
      ...prev,
      child: [...prev.child, subItemToAdd],
    }));
    setNewSubItem({ name: "", criteria: "" });
  };

  const handleDeleteSubItem = (index) => {
    const updated = formData.child.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, child: updated }));
    if (updated.length === 0) setShowSubItemForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!formData.name.trim() || !formData.criteria.toString().trim()) return;

    const allChildren = [...formData.child];
    if (newSubItem.name?.trim() && newSubItem.criteria?.toString().trim()) {
      allChildren.push({
        name: newSubItem.name.trim(),
        criteria: Number(newSubItem.criteria),
      });
    }

    const hasInvalidChild = allChildren.some(
      (item) => !item.name?.trim() || item.criteria === "" || item.criteria === null
    );
    if (hasInvalidChild) return;

    const payload = {
      name: formData.name.trim(),
      criteria: Number(formData.criteria),
      child: allChildren.length > 0 ? allChildren : [],
    };

    try {
      setLoading(true);
      const token = sessionStorage.getItem('auth_token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      let res;

      // Use stored vehicle_checklist_id for update - FIXED: Use PUT API
      if (checklistId && checklistId !== 'null') {
        res = await axiosInstance.put(
          `/ems/vehicle-checklist/${checklistId}`,
          payload,
          { headers }
        );
        sessionStorage.removeItem('vehicle_checklist_id'); // Clean up
      } else {
        payload.vehicle_id = vehicleId;
        res = await axiosInstance.post(
          "/ems/manage-specific-vehicle-checklist",
          payload,
          { headers }
        );
      }

      alert(res.data?.message || "Saved successfully");
      if (refreshData) refreshData();
      onClose();
    } catch (err) {
      console.error("API Error:", err);
      alert(err.response?.data?.message || "Error saving checklist item");
    } finally {
      setLoading(false);
    }
  };

  const shouldShowError = (field) => validated && !field.toString().trim();

  // - The item already has subitems (only parent items can have subitems)
  const canAddNewSubitem = !isSubitem && formData.child.length === 0;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {checklistId && checklistId !== 'null' ? "Edit" : "Add"} Vehicle Checklist Item
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${shouldShowError(formData.name) ? "is-invalid" : ""}`}
                    required
                  />
                  {shouldShowError(formData.name) && (
                    <div className="invalid-feedback">Name is required</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label>Criteria <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    name="criteria"
                    value={formData.criteria}
                    onChange={handleChange}
                    className={`form-control ${shouldShowError(formData.criteria) ? "is-invalid" : ""}`}
                    required
                  />
                  {shouldShowError(formData.criteria) && (
                    <div className="invalid-feedback">Criteria is required</div>
                  )}
                </div>
              </div>

              {/* Existing Subitems */}
              {formData.child.length > 0 && (
                <div className="mt-4">
                  <h6>Subitems</h6>
                  {formData.child.map((sub, i) => (
                    <div key={i} className="row align-items-center border-top pt-3">
                      <div className="col-md-5">
                        <input
                          type="text"
                          value={sub.name}
                          onChange={(e) => handleExistingSubItemChange(i, e)}
                          name="name"
                          className={`form-control ${validated && !sub.name.trim() ? "is-invalid" : ""}`}
                        />
                      </div>
                      <div className="col-md-5">
                        <input
                          type="number"
                          value={sub.criteria}
                          onChange={(e) => handleExistingSubItemChange(i, e)}
                          name="criteria"
                          className={`form-control ${validated && !sub.criteria ? "is-invalid" : ""}`}
                        />
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteSubItem(i)}
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Subitem Section - Only for parent items with no existing subitems */}
                {canAddNewSubitem && !showSubItemForm && (
                  <button
                    type="button"
                    className="btn btn-outline-primary mt-3"
                    onClick={() => setShowSubItemForm(true)}
                  >
                    <i className="ti ti-circle-plus me-2"></i>
                    Add Subitem
                  </button>
                )}

                {/* New Subitem Input Form - Show only when adding */}
                {showSubItemForm && canAddNewSubitem && (
                  <div className="row mt-3 border p-3 rounded bg-light">
                    <div className="col-md-6">
                      <input
                        type="text"
                        placeholder="Subitem Name"
                        value={newSubItem.name}
                        onChange={handleSubItemChange}
                        name="name"
                        className={`form-control ${validated && !newSubItem.name.trim() ? "is-invalid" : ""}`}
                      />
                      {validated && !newSubItem.name.trim() && (
                        <div className="invalid-feedback">Name is required</div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <input
                        type="number"
                        placeholder="Criteria"
                        value={newSubItem.criteria}
                        onChange={handleSubItemChange}
                        name="criteria"
                        className={`form-control ${validated && !newSubItem.criteria ? "is-invalid" : ""}`}
                      />
                      {validated && !newSubItem.criteria && (
                        <div className="invalid-feedback">Criteria is required</div>
                      )}
                    </div>
                    <div className="col-md-2 d-flex border-0 ">
                     
                      {/* Cancel Icon */}
                      <button
                        type="button"
                        className="btn btn-danger "
                        onClick={() => {
                          setShowSubItemForm(false);
                          setNewSubItem({ name: "", criteria: "" });
                        }}
                        title="Cancel"
                      >
                        <i className="ti ti-trash "></i>
                      </button>
                    </div>
                  </div>
                )}

              
            </form>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : checklistId ? "Update" : "Save"} Item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDailychecklistModal;